import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Check, ChevronDown, ChevronRight, ChevronUp, Download, HeadphoneOff, Headphones, LogOut, Mic, MicOff, Pencil, PhoneOff, Settings, ShieldAlert, Signal, SunMoon } from 'lucide-react'
import { isPlatformStaff } from '@/lib/moderationApi'
import { useAppStore } from '@/store/app'
import { useAuthStore } from '@/store/auth'
import { useGuildStore } from '@/store/guilds'
import { usePresenceStore, useSelfStatus } from '@/store/presence'
import { channelName, useSocialStore } from '@/store/social'
import { useVoiceStore } from '@/store/voice'
import { cx } from '@/lib/cx'
import { avatarUrl } from '@/lib/avatar'
import { guildApi } from '@/lib/guildApi'
import { P, channelPermissions, has } from '@/lib/permissions'
import { isDesktop } from '@/lib/desktop'
import { coarsePointer } from '@/lib/mobile'
import { SELF_STATUSES, STATUS_HINT, STATUS_LABEL } from '@/lib/presence'
import type { SelfStatus } from '@/types/api'
import { AudioMenu, type AudioMenuKind } from './voice/AudioMenu'
import { VoiceActions } from './voice/VoiceActions'
import { WhatsNew } from './releases/WhatsNew'
import { Avatar } from './Avatar'
import { CustomStatusModal } from './CustomStatusModal'
import { StatusIcon } from './StatusIcon'
import styles from './UserPanel.module.css'
import { StyledName } from './StyledName'
import { SelfCard } from './SelfCard'

/** Что показать под именем, пока шлюз не подключён; после — свой статус. */
const statusLabel: Record<string, string> = {
  connecting: 'Подключение…',
  identifying: 'Подключение…',
  reconnecting: 'Переподключение…',
  closed: 'Нет связи со шлюзом',
  idle: 'Не подключено',
}

export function UserPanel() {
  const gateway = useAppStore((s) => s.gateway)
  const toggleTheme = useAppStore((s) => s.toggleTheme)
  const openModeration = useAppStore(s => s.openModeration)
  const openSettings = useAppStore((s) => s.openSettings)
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const connection = useVoiceStore((s) => s.connection)
  const afk = useVoiceStore(s => s.afk)
  const selfMute = useVoiceStore((s) => s.selfMute)
  const selfDeaf = useVoiceStore((s) => s.selfDeaf)
  const toggleMute = useVoiceStore((s) => s.toggleMute)
  const toggleDeaf = useVoiceStore((s) => s.toggleDeaf)
  const leaveVoice = useVoiceStore((s) => s.leave)
  const myQuality = useVoiceStore((s) => (user ? s.quality[user.id] : undefined))
  // зелёное кольцо, пока говорю, — как у участников в голосовой комнате; выключенный микрофон стор гасит сам
  const speaking = useVoiceStore((s) => Boolean(user && s.connection && s.speaking[user.id]))
  const rtt = useVoiceStore((s) => s.rtt)
  const [pingOpen, setPingOpen] = useState(false)
  const openGuild = useAppStore((s) => s.openGuild)
  const setView = useAppStore((s) => s.setView)
  const voiceChannel = useGuildStore((s) => (connection?.guildId ? s.channels[connection.guildId]?.[connection.channelId] : undefined))
  const voiceGuild = useGuildStore((s) => (connection?.guildId ? s.guilds[connection.guildId] : undefined))
  const dmChannel = useSocialStore((s) => (connection && !connection.guildId ? s.channels[connection.channelId] : undefined))
  /*
   * Серверный мут и звук ставит модератор, своя кнопка их не снимает. Админ с правом их снять делает это
   * той же кнопкой — иначе после чужого мута он оставался без голоса (меню себя это не предлагало).
   */
  const myVoice = useGuildStore((s) => (user && connection?.guildId ? s.voiceStates[connection.guildId]?.[user.id] : undefined))
  const myMember = useGuildStore((s) => (user && connection?.guildId ? s.members[connection.guildId]?.[user.id] : undefined))
  const voicePerms = voiceGuild && voiceChannel && myMember ? channelPermissions(voiceGuild, myMember, voiceChannel) : 0n
  const serverMuted = Boolean(myVoice?.mute)
  const serverDeaf = Boolean(myVoice?.deaf)
  const liftServer = (patch: { mute?: false; deaf?: false }) => {
    if (user && connection?.guildId) void guildApi.updateMember(connection.guildId, user.id, patch).catch(() => undefined)
  }
  const unmuteServer = serverMuted && has(voicePerms, P.MUTE_MEMBERS) ? () => liftServer({ mute: false }) : null
  const undeafServer = serverDeaf && has(voicePerms, P.DEAFEN_MEMBERS) ? () => liftServer({ deaf: false }) : null
  const micTitle = afk ? 'Микрофон выключен в AFK' : serverMuted ? (unmuteServer ? 'Снять серверный мут' : 'Вас заглушили на сервере') : selfMute ? 'Включить микрофон' : 'Выключить микрофон'
  const soundTitle = afk ? 'Звук выключен в AFK' : serverDeaf ? (undeafServer ? 'Включить звук, выключенный сервером' : 'Звук выключен сервером') : selfDeaf ? 'Включить звук' : 'Выключить звук'
  const [menu, setMenu] = useState<null | 'profile' | AudioMenuKind>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  /*
   * Статусы — подменю сбоку. Мышью оно открывается наведением и закрывается с задержкой: иначе
   * по пути от строки к подменю курсор пересекает щель, и оно успевало закрыться. Пальцем — нажатием.
   */
  const [statusOpen, setStatusOpen] = useState(false)
  const statusTimer = useRef<number | undefined>(undefined)
  const hoverStatus = !coarsePointer
  const openStatus = () => {
    window.clearTimeout(statusTimer.current)
    setStatusOpen(true)
  }
  const closeStatusSoon = () => {
    window.clearTimeout(statusTimer.current)
    statusTimer.current = window.setTimeout(() => setStatusOpen(false), 200)
  }
  useEffect(() => () => window.clearTimeout(statusTimer.current), [])
  const chosen = usePresenceStore((s) => s.status)
  const customText = usePresenceStore((s) => s.customText)
  const setStatus = usePresenceStore((s) => s.setStatus)
  const selfStatus = useSelfStatus()
  const [editingCustom, setEditingCustom] = useState(false)
  const [statusError, setStatusError] = useState<string | null>(null)

  useEffect(() => {
    if (!menu) return
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenu(null)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [menu])

  const name = user?.global_name ?? user?.username ?? '…'
  // своя игра — вместо «В сети»: так видно, что друзья её тоже видят
  const myGame = useSocialStore((s) => (user ? s.activities[user.id] : undefined))
  const online = gateway === 'ready'
  // под именем: связь со шлюзом, пока её нет, затем свой статус текстом, игра или статус словами
  const line = !online ? (statusLabel[gateway] ?? gateway) : customText || myGame?.name || STATUS_LABEL[selfStatus]
  const lineTitle = online && customText ? customText : myGame && online ? `Играет в ${myGame.name}` : undefined

  const choose = (next: SelfStatus) => {
    setMenu(null)
    setStatusError(null)
    if (next === chosen) return
    void setStatus(next).catch(() => {
      setStatusError('Не удалось сменить статус. Попробуйте ещё раз.')
      setMenu('profile')
    })
  }

  const voiceLabel = connection?.status === 'connected' ? 'Голос подключён' : connection?.status === 'reconnecting' ? 'Переподключение…' : 'Подключение…'
  // качество и задержку показываем по клику на значок связи: постоянно они только мешают
  const qualityLabel = myQuality === 'excellent' ? 'связь отличная' : myQuality === 'good' ? 'связь хорошая' : myQuality === 'poor' ? 'связь плохая' : myQuality === 'lost' ? 'связь потеряна' : null
  const details = connection?.status === 'connected'
    ? [qualityLabel, rtt != null ? `${rtt} мс` : null].filter(Boolean).join(' · ')
    : ''

  /*
   * Док лежит поверх колонок, поэтому сам места им не занимает. Его высота меняется:
   * при подключении к голосу сверху появляется полоса связи. Публикуем измеренную высоту
   * в переменную, и колонки резервируют ровно столько, сколько нужно.
   */
  const dockRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = dockRef.current
    if (!el) return
    const root = document.documentElement
    const publish = () => root.style.setProperty('--panel-height', `${el.offsetHeight}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(el)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--panel-height')
    }
  }, [])

  return (
    <div className={styles.dock} ref={dockRef}>
    {/* «Что нового» — над голосом и строкой пользователя; в голосе карточка прячется сама */}
    <WhatsNew />
    {connection ? (
      <div className={styles.voice}>
      <div className={styles.voiceBar} data-status={connection.status} data-quality={myQuality ?? ''}>
        <button
          type="button"
          className={styles.voiceSignalButton}
          data-tooltip={details ? (pingOpen ? 'Скрыть качество связи' : details) : 'Качество связи'}
          aria-label="Качество связи"
          aria-pressed={pingOpen}
          onClick={() => setPingOpen((v) => !v)}
        >
          <Signal size={16} className={styles.voiceSignal} />
        </button>
        <button type="button" className={styles.voiceInfo} data-tooltip={connection.guildId ? 'Открыть голосовой канал' : 'Открыть звонок'} onClick={() => (connection.guildId ? openGuild(connection.guildId, connection.channelId) : setView({ kind: 'home', channelId: connection.channelId }))}>
          <span className={styles.voiceText}>
            <span className={styles.voiceStatus}>{connection.guildId ? voiceLabel : voiceLabel.replace('Голос подключён', 'Звонок идёт')}</span>
            <span className={styles.voiceChannel}>
              {pingOpen && details ? details : connection.guildId ? `${voiceChannel?.name ?? '…'} / ${voiceGuild?.name ?? ''}` : dmChannel ? channelName(dmChannel) : 'Личные сообщения'}
            </span>
          </span>
        </button>
        <button type="button" className={styles.action} data-tooltip="Отключиться" aria-label="Отключиться от голосового канала" onClick={() => void leaveVoice()}>
          <PhoneOff size={18} />
        </button>
      </div>
      <VoiceActions />
      </div>
    ) : null}
    <div className={styles.panel} ref={menuRef}>
      <button type="button" className={styles.user} data-tooltip={`@${user?.username ?? ''}`} onClick={() => { setStatusOpen(false); setMenu((v) => (v === 'profile' ? null : 'profile')) }} aria-haspopup="menu" aria-expanded={menu === 'profile'}>
        <Avatar initial={name.slice(0, 1).toUpperCase()} color="#5865F2" size={32} status={online ? selfStatus : 'offline'} speaking={speaking} className={styles.avatar} src={user ? avatarUrl(user.id, user.avatar) : undefined} decoration={user?.avatar_decoration_data} />
        <span className={styles.text}>
          <StyledName name={name} style={user?.display_name_styles} className={styles.name} />
          <span className={styles.status} data-tooltip={lineTitle}>{line}</span>
        </span>
      </button>
      {menu === 'profile' ? (
        <div className={cx(styles.menu, 'klichat-popover')} role="menu" style={{ '--popover-origin': 'bottom left' } as CSSProperties}>
          {/* своя карточка сверху, как в Discord: баннер, аватар с рамкой и статусом, пузырь своего статуса */}
          <SelfCard className={styles.me} bubble={customText || 'О чём думаешь?'} placeholder={!customText} onBubble={() => { setMenu(null); setEditingCustom(true) }} />
          <div className={styles.box}>
            <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { openSettings('profile'); setMenu(null) }}>
              <Pencil size={18} /> Редактировать профиль
            </button>
            <div className={styles.boxDivider} />
            {/* статусы — отдельным меню сбоку (на телефоне — раскрываются здесь же), как в Discord */}
            <div className={styles.statusWrap} onMouseEnter={hoverStatus ? openStatus : undefined} onMouseLeave={hoverStatus ? closeStatusSoon : undefined}>
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={statusOpen}
                className={cx(styles.menuItem, statusOpen && styles.statusRowOpen)}
                onClick={() => (hoverStatus ? openStatus() : setStatusOpen((v) => !v))}
              >
                <StatusIcon status={chosen} size={12} className={styles.statusIcon} />
                <span className={styles.statusRowLabel}>{STATUS_LABEL[chosen]}</span>
                <ChevronRight size={16} className={styles.statusChevron} />
              </button>
              {statusOpen ? (
                <div className={cx(styles.statusFlyout, 'klichat-popover')} role="menu" aria-label="Статус" style={{ '--popover-origin': 'top left' } as CSSProperties}>
                  {SELF_STATUSES.map((st) => (
                    <button key={st} type="button" role="menuitemradio" aria-checked={chosen === st} aria-label={STATUS_HINT[st] ? `${STATUS_LABEL[st]}. ${STATUS_HINT[st]}` : STATUS_LABEL[st]} className={cx(styles.menuItem, styles.statusItem)} onClick={() => choose(st)}>
                      <StatusIcon status={st} size={12} className={styles.statusIcon} />
                      <span className={styles.statusText}>
                        <span>{STATUS_LABEL[st]}</span>
                        {STATUS_HINT[st] ? <span className={styles.statusHint}>{STATUS_HINT[st]}</span> : null}
                      </span>
                      {chosen === st ? <Check size={16} className={styles.statusCheck} /> : null}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            {statusError ? <p className={styles.menuError} role="alert">{statusError}</p> : null}
          </div>
          <div className={styles.box}>
            {isPlatformStaff(user?.flags ?? 0) ? <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { openModeration(); setMenu(null) }}><ShieldAlert size={18} /> Модерация</button> : null}
            <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { toggleTheme(); setMenu(null) }}>
              <SunMoon size={18} /> Сменить тему
            </button>
            {isDesktop ? null : (
              <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { openSettings('desktop'); setMenu(null) }}>
                <Download size={18} /> Скачать приложение
              </button>
            )}
            <button type="button" role="menuitem" className={`${styles.menuItem} ${styles.menuDanger}`} onClick={() => void signOut()}>
              <LogOut size={18} /> Выйти
            </button>
          </div>
        </div>
      ) : null}
      {editingCustom ? <CustomStatusModal onClose={() => setEditingCustom(false)} /> : null}
      {menu === 'input' || menu === 'output' ? (
        <div className={cx(styles.audioMenu, 'klichat-popover')} style={{ '--popover-origin': 'bottom right' } as CSSProperties}>
          <AudioMenu kind={menu} onClose={() => setMenu(null)} />
        </div>
      ) : null}
      <div className={styles.actions}>
        <div className={styles.split} data-open={menu === 'input' ? '' : undefined}>
          <button type="button" className={cx(styles.action, styles.splitMain, (afk || selfMute || serverMuted) && styles.actionOff)} data-tooltip={micTitle} aria-label={micTitle} disabled={afk} aria-pressed={afk || selfMute || serverMuted} onClick={() => (unmuteServer ? unmuteServer() : void toggleMute())}>
            {afk || selfMute || serverMuted ? <MicOff size={20} /> : <Mic size={20} />}
          </button>
          <button type="button" className={styles.caret} data-tooltip="Микрофон: устройство и громкость" aria-label="Настройки микрофона" aria-haspopup="menu" aria-expanded={menu === 'input'} onClick={() => setMenu((v) => (v === 'input' ? null : 'input'))}>
            {menu === 'input' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
        <div className={styles.split} data-open={menu === 'output' ? '' : undefined}>
          <button type="button" className={cx(styles.action, styles.splitMain, (afk || selfDeaf || serverDeaf) && styles.actionOff)} data-tooltip={soundTitle} aria-label={soundTitle} disabled={afk} aria-pressed={afk || selfDeaf || serverDeaf} onClick={() => (undeafServer ? undeafServer() : void toggleDeaf())}>
            {afk || selfDeaf || serverDeaf ? <HeadphoneOff size={20} /> : <Headphones size={20} />}
          </button>
          <button type="button" className={styles.caret} data-tooltip="Звук: устройство и громкость" aria-label="Настройки звука" aria-haspopup="menu" aria-expanded={menu === 'output'} onClick={() => setMenu((v) => (v === 'output' ? null : 'output'))}>
            {menu === 'output' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
        </div>
        <button type="button" className={styles.action} data-tooltip="Настройки пользователя" aria-label="Настройки пользователя" onClick={() => { setMenu(null); openSettings('account') }}>
          <Settings size={20} />
        </button>
      </div>
    </div>
    </div>
  )
}
